import _ from 'lodash';
import AppRouter from 'common/AppRouter';
import { PR_STATES } from 'views/root/views/home/views/activity/views/pull-requests/pr.service';
import { METRICS_GROUP_BY_OPTIONS } from 'views/root/views/home/views/performance/views/CustomReport/custom-report.serivce.js';
import {
  metricNames,
  TIME_TO_CUSTOM_STAGES_REGEX
} from '@linearb/linjs-common';

export const CYCLE_TIME_METRICS = [
  metricNames.CYCLE_TIME,
  metricNames.TIME_TO_PR,
  metricNames.TIME_TO_REVIEW,
  metricNames.REVIEW_TIME,
  metricNames.TIME_TO_PROD
];

export const extractDatesFromMetricPoint = ({
  category: { after: from, before: to }
}) => ({ from, to });

export const metricPointRedirectStateFilters = ({ onlyMerged = false } = {}) =>
  _.values(
    _.omit(PR_STATES, [
      PR_STATES.HAS_NO_PR.key,
      PR_STATES.DRAFT.key,
      ...(onlyMerged ? [PR_STATES.INITIATED.key, PR_STATES.IN_REVIEW.key] : [])
    ])
  )
    .map(({ filter }) => filter)
    .join(',');

export const mapMetricPointToGroupByRedirectParam = point => {
  const { groupId, metricsGroupByKey } = _.get(
    point,
    'series.options.customState',
    {}
  );
  switch (metricsGroupByKey) {
    case METRICS_GROUP_BY_OPTIONS.TEAM:
      return { teams: groupId };
    case METRICS_GROUP_BY_OPTIONS.CONTRIBUTOR:
      return { contributors: groupId, author: groupId };
    case METRICS_GROUP_BY_OPTIONS.REPOSITORY:
      return { repositories: groupId };
    case METRICS_GROUP_BY_OPTIONS.LABEL:
      return { labels: groupId };
    default:
      return {};
  }
};

export const isCustomStagesMetric = metricName => {
  return (
    TIME_TO_CUSTOM_STAGES_REGEX.test(metricName) ||
    metricName === metricNames.BRANCH_TIME_TO_RELEASE
  );
};

export const DEFAULT_TIME_RANGE_KEY = 'time_range';

export const getRedirectParamsByMetric =
  (isQualityMetricsFlowFF, isActivityReleasesEnabled) =>
  ({ metricName }) => {
    let redirectParams = {};

    if (isCustomStagesMetric(metricName)) {
      redirectParams = {
        [metricName]: point => {
          const { from, to } = extractDatesFromMetricPoint(point);
          return {
            path: AppRouter.BRANCH.path,
            qs: {
              metric_name: metricName,
              [DEFAULT_TIME_RANGE_KEY]: `${from},${to}`,
              order_by: `${metricName},desc`
            },
            state: {
              isCycleTimeColsExpanded: true,
              isWorkBreakdownColsExpanded: false
            }
          };
        }
      };
    } else {
      redirectParams = mapMetricPointToRedirectParams(
        isQualityMetricsFlowFF,
        isActivityReleasesEnabled
      );
    }

    return _.get(redirectParams, `${metricName}`, _.noop());
  };

/**
 * @param {boolean} isQualityMetricsFlowFF
 * @param {boolean} isActivityReleasesEnabled
 * @returns {object}
 */
export const mapMetricPointToRedirectParams = (
  isQualityMetricsFlowFF,
  isActivityReleasesEnabled
) => {
  return {
    // Delivery Metrics
    [metricNames.CYCLE_TIME]: point => {
      const { from, to } = extractDatesFromMetricPoint(point);
      return {
        path: AppRouter.BRANCH.path,
        qs: {
          metric_name: metricNames.CYCLE_TIME,
          cycle_time_updated: `${from},${to}`,
          order_by: 'cycle_time,desc',
          'PR State': metricPointRedirectStateFilters({ onlyMerged: true })
        },
        state: {
          isCycleTimeColsExpanded: true,
          isWorkBreakdownColsExpanded: false
        }
      };
    },
    [metricNames.TIME_TO_PR]: point => {
      // Coding
      const { from, to } = extractDatesFromMetricPoint(point);
      return {
        path: AppRouter.BRANCH.path,
        qs: {
          metric_name: metricNames.TIME_TO_PR,
          coding_ended: `${from},${to}`,
          order_by: 'time_to_pr,desc',
          'PR State': metricPointRedirectStateFilters()
        },
        state: {
          isCycleTimeColsExpanded: true,
          isWorkBreakdownColsExpanded: false
        }
      };
    },
    [metricNames.TIME_TO_REVIEW]: point => {
      // Pickup
      const { from, to } = extractDatesFromMetricPoint(point);
      return {
        path: AppRouter.BRANCH.path,
        qs: {
          metric_name: metricNames.TIME_TO_REVIEW,
          reviewed: `${from},${to}`,
          order_by: 'time_to_review,desc',
          'PR State': metricPointRedirectStateFilters()
        },
        state: {
          isCycleTimeColsExpanded: true,
          isWorkBreakdownColsExpanded: false
        }
      };
    },
    ...[
      metricNames.REVIEW_TIME,
      metricNames.BRANCH_TIME_TO_APPROVE,
      metricNames.BRANCH_TIME_TO_MERGE
    ].reduce((acc, current) => {
      acc[current] = point => {
        // Review
        const { from, to } = extractDatesFromMetricPoint(point);
        return {
          path: AppRouter.BRANCH.path,
          qs: {
            metric_name: current,
            merged: `${from},${to}`,
            order_by: 'review_time,desc',
            'PR State': metricPointRedirectStateFilters()
          },
          state: {
            isCycleTimeColsExpanded: true,
            isWorkBreakdownColsExpanded: false
          }
        };
      };
      return acc;
    }, {}),

    [metricNames.TIME_TO_PROD]: point => {
      // Deploy Time
      const { from, to } = extractDatesFromMetricPoint(point);
      return {
        path: AppRouter.BRANCH.path,
        qs: {
          metric_name: metricNames.TIME_TO_PROD,
          released: `${from},${to}`,
          order_by: 'time_to_prod,desc',
          'PR State': metricPointRedirectStateFilters()
        },
        state: {
          isCycleTimeColsExpanded: true,
          isWorkBreakdownColsExpanded: false
        }
      };
    },
    ...(isActivityReleasesEnabled && {
      [metricNames.RELEASES_COUNT]: point => {
        const { from, to } = extractDatesFromMetricPoint(point);
        return {
          path: AppRouter.RELEASES.path,
          qs: {
            released_at: `${from},${to}`,
            order_by: 'published_at,desc',
            stages: 'release',
            connected_branches: 'true',
            metric_name: metricNames.RELEASES_COUNT
          }
        };
      }
    }),
    ...(isQualityMetricsFlowFF && {
      // Quality Metrics
      [metricNames.PR_MERGED_WITHOUT_REVIEW]: point => {
        const { from, to } = extractDatesFromMetricPoint(point);
        return {
          path: AppRouter.PULL_REQUESTS.path,
          qs: {
            Size: '20-100',
            Reviewed: 1,
            merged: `${from},${to}`,
            order_by: 'updated_at,desc'
          }
        };
      },
      [metricNames.PR_NEW]: point => {
        const { from, to } = extractDatesFromMetricPoint(point);
        return {
          path: AppRouter.PULL_REQUESTS.path,
          qs: {
            created: `${from},${to}`,
            order_by: 'created_at,desc'
          }
        };
      },
      [metricNames.PR_MERGED]: point => {
        const { from, to } = extractDatesFromMetricPoint(point);
        return {
          path: AppRouter.PULL_REQUESTS.path,
          qs: {
            merged: `${from},${to}`,
            order_by: 'pr.size.total,desc'
          }
        };
      },
      [metricNames.PR_SIZE]: point => {
        const { from, to } = extractDatesFromMetricPoint(point);
        return {
          path: AppRouter.PULL_REQUESTS.path,
          qs: {
            merged: `${from},${to}`,
            order_by: 'pr.size.total,desc'
          }
        };
      },
      [metricNames.REVIEW_DEPTH]: point => {
        const { from, to } = extractDatesFromMetricPoint(point);
        return {
          path: AppRouter.PULL_REQUESTS.path,
          qs: {
            merged: `${from},${to}`,
            Reviewed: 0,
            order_by: 'comment_count,desc'
          }
        };
      },
      // WorkBreakdown Metrics
      [metricNames.NEW_WORK]: point => {
        const { from, to } = extractDatesFromMetricPoint(point);
        return {
          path: AppRouter.BRANCH.path,
          qs: {
            committed: `${from},${to}`,
            order_by: 'new_work,desc',
            metric_name: metricNames.NEW_WORK
          },
          state: {
            isCycleTimeColsExpanded: false,
            isWorkBreakdownColsExpanded: true
          }
        };
      },
      [metricNames.REWORK]: point => {
        const { from, to } = extractDatesFromMetricPoint(point);
        return {
          path: AppRouter.BRANCH.path,
          qs: {
            committed: `${from},${to}`,
            order_by: 'rework,desc',
            metric_name: metricNames.REWORK
          },
          state: {
            isCycleTimeColsExpanded: false,
            isWorkBreakdownColsExpanded: true
          }
        };
      },
      [metricNames.REFACTOR]: point => {
        const { from, to } = extractDatesFromMetricPoint(point);
        return {
          path: AppRouter.BRANCH.path,
          qs: {
            committed: `${from},${to}`,
            order_by: 'refactor,desc',
            metric_name: metricNames.REFACTOR
          },
          state: {
            isCycleTimeColsExpanded: false,
            isWorkBreakdownColsExpanded: true
          }
        };
      }
    }),

    [metricNames.PM_CFR]: point => {
      const { from, to } = extractDatesFromMetricPoint(point);
      return {
        path: AppRouter.INCIDENTS.path,
        qs: {
          in_progress_between: `${from},${to}`
        },
        state: {
          metric_name: metricNames.PM_CFR
        }
      };
    },
    [metricNames.PM_MTTR]: point => {
      const { from, to } = extractDatesFromMetricPoint(point);
      return {
        path: AppRouter.INCIDENTS.path,
        qs: {
          in_progress_between: `${from},${to}`,
          status: 'closed'
        },
        state: {
          metric_name: metricNames.PM_MTTR
        }
      };
    }
  };
};
